/**
 * GaslessCheckout Component
 * Handles the gasless booking flow via ERC-4337 Account Abstraction.
 *
 * Flow:
 * 1. Guest clicks "Book Gasless" -> creates smart account + pending booking
 * 2. Shows signing step (guest approves with their EOA)
 * 3. Submits signed UserOp -> paymaster sponsors gas
 * 4. Booking confirmed - guest paid $0 gas
 */

import { useState, useEffect, useCallback } from 'react';
import { useAppKitAccount } from '@reown/appkit/react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
  Shield, CheckCircle2, Loader2, ArrowLeft, Zap, Wallet,
  Fuel, ExternalLink, Copy, Sparkles, Key
} from 'lucide-react';
import { toast } from 'sonner';
import { aaApi } from '../lib/aaApi';

const STEPS = [
  { key: 'init', label: 'Creating Smart Account', icon: Wallet },
  { key: 'sign', label: 'Sign Transaction', icon: Shield },
  { key: 'submit', label: 'Submitting On-Chain', icon: Zap },
  { key: 'confirmed', label: 'Booking Confirmed', icon: CheckCircle2 },
];

function truncateAddress(addr) {
  if (!addr) return '';
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

export function GaslessCheckout({
  bookingState,
  pricingInfo,
  hotel,
  hotelId,
  onSuccess,
  onCancel,
}) {
  const { address: eoaAddress, isConnected } = useAppKitAccount();
  const [step, setStep] = useState('init'); // init -> sign -> submit -> confirmed
  const [progress, setProgress] = useState(0);
  const [gaslessData, setGaslessData] = useState(null);
  const [error, setError] = useState(null);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [sessionKeyGranted, setSessionKeyGranted] = useState(false);
  const [grantingKey, setGrantingKey] = useState(false);

  // Step 1: Create smart account + pending booking
  const initGaslessBooking = useCallback(async () => {
    if (!eoaAddress || !bookingState.guest_email) return;
    setStep('init');
    setProgress(15);
    setError(null);

    try {
      const result = await aaApi.createGaslessBooking({
        eoa_address: eoaAddress,
        guest_email: bookingState.guest_email,
        hotel_id: hotelId,
        room_id: bookingState.room_id,
        guest_name: bookingState.guest_name,
        guest_phone: bookingState.guest_phone || '',
        check_in: bookingState.check_in,
        check_out: bookingState.check_out,
        guests: parseInt(bookingState.guests) || 1,
        special_requests: bookingState.special_requests || '',
        total_price: pricingInfo?.final_price || 0,
      });

      setGaslessData(result);
      setProgress(35);
      setStep('sign');
    } catch (err) {
      setError(err.message);
      toast.error('Failed to prepare gasless booking');
    }
  }, [eoaAddress, bookingState, hotelId, pricingInfo]);

  useEffect(() => {
    initGaslessBooking();
  }, [initGaslessBooking]);

  // Step 2: Guest signs (in demo mode, auto-proceed)
  const handleSign = async () => {
    setStep('submit');
    setProgress(65);

    try {
      // In full mode, guest would sign the UserOp with their EOA via wagmi
      // In demo mode (no bundler), we skip signing and confirm directly
      const bookingId = gaslessData?.booking?.id;
      if (!bookingId) throw new Error('No booking ID');

      setProgress(80);

      // Confirm the booking
      const result = await aaApi.confirmGaslessBooking(bookingId);

      setProgress(100);
      setStep('confirmed');
      setConfirmedBooking(result);

      toast.success('Booking confirmed gaslessly!');
    } catch (err) {
      setError(err.message);
      toast.error('Failed to confirm booking');
    }
  };

  const handleDone = () => {
    if (onSuccess && confirmedBooking) {
      onSuccess({
        booking: confirmedBooking.booking,
        tx_hash: confirmedBooking.tx_hash,
        user_op_hash: confirmedBooking.user_op_hash,
        gas_cost: '0',
      });
    }
  };

  const handleGrantSessionKey = async () => {
    if (!confirmedBooking?.booking?.id || !gaslessData?.smart_account) return;
    setGrantingKey(true);
    try {
      await aaApi.grantSessionKey({
        smart_account: gaslessData.smart_account.smart_account_address,
        eoa_address: eoaAddress,
        booking_id: confirmedBooking.booking.id,
        permissions: ['nft_upgrade'],
        max_uses: 1,
      });
      setSessionKeyGranted(true);
      toast.success('Session key granted! Your booking NFT will auto-convert at checkout.');
    } catch (err) {
      toast.error('Failed to grant session key');
    } finally {
      setGrantingKey(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  if (!isConnected) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6" data-testid="gasless-no-wallet">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-4">
            <Wallet className="w-12 h-12 mx-auto text-muted-foreground" />
            <h2 className="text-lg heading-font">Wallet Not Connected</h2>
            <p className="text-sm text-muted-foreground">
              Connect your wallet via email or social login to use gasless booking.
            </p>
            <Button onClick={onCancel} variant="outline" data-testid="gasless-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4 sm:p-6" data-testid="gasless-checkout">
      <Card className="max-w-lg w-full border-2 border-primary/20 shadow-lg">
        <CardContent className="p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[hsl(184,78%,34%)]/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[hsl(184,78%,34%)]" />
              </div>
              <div>
                <h2 className="text-lg heading-font" data-testid="gasless-title">Gasless Booking</h2>
                <p className="text-xs text-muted-foreground">Powered by ERC-4337 Account Abstraction</p>
              </div>
            </div>
            {step !== 'confirmed' && (
              <Button variant="ghost" size="sm" onClick={onCancel} data-testid="gasless-cancel-btn">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Progress */}
          <div className="space-y-2">
            <Progress value={progress} className="h-1.5" data-testid="gasless-progress" />
            <div className="flex justify-between">
              {STEPS.map((s, i) => {
                const StepIcon = s.icon;
                const isActive = s.key === step;
                const isDone = STEPS.findIndex(x => x.key === step) > i;
                return (
                  <div key={s.key} className="flex flex-col items-center gap-1" data-testid={`gasless-step-${s.key}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs transition-colors ${
                      isDone ? 'bg-emerald-500/10 text-emerald-600' :
                      isActive ? 'bg-primary/10 text-primary' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : <StepIcon className="w-3.5 h-3.5" />}
                    </div>
                    <span className={`text-[10px] ${isActive ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Booking Summary */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Hotel</span>
              <span className="heading-font">{hotel?.name || hotelId}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Dates</span>
              <span>{bookingState.check_in} to {bookingState.check_out}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total</span>
              <span className="heading-font mono">${pricingInfo?.final_price || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Gas Fee</span>
              <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300 bg-emerald-50">
                <Fuel className="w-3 h-3 mr-1" /> $0.00 (Sponsored)
              </Badge>
            </div>
          </div>

          {/* Smart Account Info */}
          {gaslessData?.smart_account && (
            <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Smart Account</p>
              <div className="flex items-center gap-2">
                <span className="mono text-xs" data-testid="gasless-smart-account">
                  {truncateAddress(gaslessData.smart_account.smart_account_address)}
                </span>
                <button
                  onClick={() => copyToClipboard(gaslessData.smart_account.smart_account_address)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Copy className="w-3 h-3" />
                </button>
                <Badge variant="outline" className="text-[10px]">
                  {gaslessData.smart_account.is_deployed ? 'Deployed' : 'Counterfactual'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">EOA:</span>
                <span className="mono text-xs">{truncateAddress(eoaAddress)}</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="gasless-error">
              {error}
            </div>
          )}

          {/* Step-specific Actions */}
          {step === 'init' && !error && (
            <div className="flex items-center justify-center gap-3 py-4" data-testid="gasless-init-loading">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Setting up your Smart Account...</p>
            </div>
          )}

          {step === 'sign' && (
            <div className="space-y-3" data-testid="gasless-sign-step">
              <div className="p-4 rounded-lg border-2 border-primary/30 bg-primary/5 space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  <h3 className="text-sm heading-font">Approve Gasless Transaction</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Sign this transaction to confirm your booking. The protocol's Paymaster will
                  cover all gas fees — you pay nothing extra.
                </p>
                {!gaslessData?.bundler_configured && (
                  <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">
                    <Sparkles className="w-3 h-3 mr-1" /> Demo Mode (Bundler not configured)
                  </Badge>
                )}
              </div>
              <Button
                className="w-full h-12"
                onClick={handleSign}
                data-testid="gasless-sign-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Sign & Confirm Booking
              </Button>
            </div>
          )}

          {step === 'submit' && (
            <div className="flex items-center justify-center gap-3 py-6" data-testid="gasless-submit-loading">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Submitting on-chain via bundler...</p>
            </div>
          )}

          {step === 'confirmed' && confirmedBooking && (
            <div className="space-y-4" data-testid="gasless-confirmed">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-lg heading-font text-emerald-700">Booking Confirmed!</h3>
                <p className="text-sm text-muted-foreground">
                  Your booking has been confirmed gaslessly. You paid $0 in gas fees.
                </p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Booking ID</span>
                  <span className="mono text-xs" data-testid="gasless-booking-id">{confirmedBooking.booking?.id}</span>
                </div>
                {confirmedBooking.tx_hash && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tx Hash</span>
                    <div className="flex items-center gap-1">
                      <span className="mono text-xs">{truncateAddress(confirmedBooking.tx_hash)}</span>
                      <a
                        href={`https://sepolia.basescan.org/tx/${confirmedBooking.tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Gas Sponsor</span>
                  <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">
                    {confirmedBooking.gas_sponsor === 'paymaster' ? 'Protocol Paymaster' : 'Demo Sponsor'}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Your Gas Cost</span>
                  <span className="heading-font text-emerald-600" data-testid="gasless-cost">$0.00</span>
                </div>
              </div>

              <Button className="w-full h-11" onClick={handleDone} data-testid="gasless-done-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" /> View Booking
              </Button>

              {/* Session Key Grant */}
              {!sessionKeyGranted ? (
                <div className="p-3 rounded-lg border border-dashed border-primary/30 bg-primary/5 space-y-2" data-testid="session-key-offer">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-primary" />
                    <p className="text-xs heading-font">Enable Auto-Checkout NFT</p>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Grant a session key so your booking NFT automatically converts to a
                    Stay Collectible at checkout. No action needed from you.
                  </p>
                  <Button
                    variant="outline" size="sm" className="w-full"
                    onClick={handleGrantSessionKey}
                    disabled={grantingKey}
                    data-testid="grant-session-key-btn"
                  >
                    <Key className="w-3 h-3 mr-1.5" />
                    {grantingKey ? 'Granting...' : 'Grant Session Key'}
                  </Button>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2" data-testid="session-key-granted">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <p className="text-xs text-emerald-700">
                    Session key active. Your NFT will auto-convert at checkout.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
